// The Askdown builder (vanilla JS): a code editor on the left, and Preview /
// Visual / JSON tabs on the right, all driven by the `askdown` core:
//
//   editor text --(debounced parse)--> model --> preview, visual cards, JSON
//   visual edit --> model --(serialize)--> editor text --> preview, JSON
//
// Owners (data-form-id) autosave through PUT /api/forms/:id and publish via
// POST /api/forms/:id/publish; anonymous visitors keep a draft in
// localStorage and can share their work through the URL hash.
import { parse, responseSchema, serialize } from '@askdown/core';
import { h, icon, clear, debounce, storage, copyText, downloadText, installMenuBehavior } from './lib/dom.js';
import { createEditor } from './lib/editor.js';
import { createPreview } from './lib/preview.js';
import { createVisual } from './lib/visual.js';
import { EXAMPLES, exampleById } from './lib/examples.js';
import { encodeHash, decodeHash } from './lib/share.js';

const SCHEMA_URL = 'https://askdown.dev/schema/v0.1.json';
const DRAFT_KEY = 'askdown-draft';
const SPLIT_KEY = 'askdown-builder-split';
const TAB_KEY = 'askdown-builder-tab';
const PARSE_DEBOUNCE = 150;
const SAVE_DEBOUNCE = 1500;

const mountEl = document.getElementById('builder');
if (mountEl) init(mountEl);

function init(mount) {
  const formId = mount.dataset.formId || null;
  const isOwner = Boolean(formId);
  const initialSource = mount.dataset.source ?? '';

  const state = {
    source: '',
    form: parse('').form,
    diagnostics: [],
    locations: { questions: {}, pages: {} },
    formId,
    status: mount.dataset.status || null,
    publicUrl: mount.dataset.publicUrl || null,
    /** saved | saving | unsaved | error | draft */
    saveState: 'saved',
    lastSavedSource: initialSource,
    tab: 'preview',
    mobile: 'editor',
    previewStale: true,
    visualStale: true,
    jsonStale: true,
  };

  // Hide the no-JS fallback; the builder takes over the page.
  for (const el of document.querySelectorAll('.builder-nojs')) el.hidden = true;
  mount.classList.add('builder--mounted');

  // --- Toolbar -------------------------------------------------------------

  const titleEl = h('h1.bt-title', 'Untitled form');
  const statusBadge = h('span.badge', { hidden: !isOwner });
  const saveIndicator = h('span.bt-save', { role: 'status', 'aria-live': 'polite' });
  const toast = h('div.toast', { role: 'status', 'aria-live': 'polite' });

  const examplesMenu = menu(
    'Examples',
    'examples',
    EXAMPLES.map((ex) =>
      h(
        'button.menu-item',
        { type: 'button', role: 'menuitem', onClick: () => loadExample(ex.id) },
        h('span.menu-item-title', ex.title),
        h('span.menu-item-desc', ex.description),
      ),
    ),
  );

  const fileInput = h('input', {
    type: 'file',
    accept: '.ask,.md,.markdown,.json,text/markdown,application/json',
    hidden: true,
    'aria-hidden': 'true',
    tabindex: '-1',
  });
  fileInput.addEventListener('change', () => {
    const file = fileInput.files && fileInput.files[0];
    if (file) importFile(file);
    fileInput.value = '';
  });

  const exportMenu = menu('Export', 'download', [
    h('button.menu-item', { type: 'button', role: 'menuitem', onClick: () => exportAskdown() }, h('span.menu-item-title', 'Download .ask'), h('span.menu-item-desc', 'The Askdown source')),
    h('button.menu-item', { type: 'button', role: 'menuitem', onClick: () => exportJson() }, h('span.menu-item-title', 'Download .json'), h('span.menu-item-desc', 'The JSON form model')),
    h('button.menu-item', { type: 'button', role: 'menuitem', onClick: () => exportResponseSchema() }, h('span.menu-item-title', 'Download JSON Schema'), h('span.menu-item-desc', 'The shape of a response')),
    h('button.menu-item', { type: 'button', role: 'menuitem', onClick: () => copySource() }, h('span.menu-item-title', 'Copy source'), h('span.menu-item-desc', 'To the clipboard')),
  ]);

  const shareButton = h('button.button.button-sm', { type: 'button', onClick: share, title: 'Copy a link to this form' }, icon('link'), h('span', 'Share'));
  const importButton = h('button.button.button-sm', { type: 'button', onClick: () => fileInput.click(), title: 'Import an .ask or .json file' }, icon('upload'), h('span', 'Import'));

  const publishButton = h('button.button.button-sm.button-primary', { type: 'button', onClick: publish }, icon('publish'), h('span', 'Publish'));
  const toggleStatusButton = h('button.menu-item', { type: 'button', role: 'menuitem', onClick: toggleStatus }, 'Close form');
  const ownerMenu = menu('More', 'chevron', [
    toggleStatusButton,
    h('a.menu-item', { href: `/forms/${formId}/responses`, role: 'menuitem' }, 'Responses'),
    h('a.menu-item', { href: '/dashboard', role: 'menuitem' }, 'All forms'),
  ]);
  ownerMenu.classList.add('menu--more');

  const urlText = h('a.bt-url-link', { target: '_blank', rel: 'noopener' });
  const urlOpen = h('a.button.button-sm.button-ghost', { target: '_blank', rel: 'noopener', 'aria-label': 'Open the form in a new tab' }, icon('external'), 'Open');
  const urlRow = h(
    'div.bt-url',
    { hidden: true },
    h('span.bt-url-label', 'Live at'),
    urlText,
    h('button.button.button-sm.button-ghost', { type: 'button', onClick: () => copy(state.publicUrl, 'Link copied'), 'aria-label': 'Copy public link' }, icon('copy'), 'Copy'),
    urlOpen,
  );

  const toolbar = h(
    'div.bt',
    h('div.bt-left', isOwner ? h('a.bt-back', { href: '/dashboard', 'aria-label': 'Back to your forms' }, icon('chevron', 'icon-back')) : null, h('div.bt-heading', titleEl, statusBadge), saveIndicator),
    h(
      'div.bt-right',
      examplesMenu,
      importButton,
      exportMenu,
      shareButton,
      isOwner ? publishButton : h('a.button.button-sm.button-primary', { href: '/login?next=%2Fbuilder' }, 'Log in to publish'),
      isOwner ? ownerMenu : null,
    ),
    fileInput,
  );

  // --- Tabs and panes ------------------------------------------------------

  const TABS = [
    { id: 'editor', label: 'Editor', icon: 'code', mobileOnly: true },
    { id: 'preview', label: 'Preview', icon: 'eye' },
    { id: 'visual', label: 'Visual', icon: 'sliders' },
    { id: 'json', label: 'JSON', icon: 'braces' },
  ];
  const tabButtons = new Map();
  const tablist = h('div.btabs', { role: 'tablist', 'aria-label': 'Builder views' });
  for (const t of TABS) {
    const b = h(
      'button.btab' + (t.mobileOnly ? '.btab--editor' : ''),
      { type: 'button', role: 'tab', id: `tab-${t.id}`, 'aria-controls': `panel-${t.id}`, 'aria-selected': 'false', tabindex: '-1', onClick: () => selectTab(t.id) },
      icon(t.icon),
      h('span', t.label),
    );
    tabButtons.set(t.id, b);
    tablist.append(b);
  }
  tablist.addEventListener('keydown', (e) => {
    const order = TABS.filter((t) => !t.mobileOnly || isMobile()).map((t) => t.id);
    const current = order.indexOf(currentTabId());
    let next = null;
    if (e.key === 'ArrowRight') next = order[(current + 1) % order.length];
    if (e.key === 'ArrowLeft') next = order[(current - 1 + order.length) % order.length];
    if (e.key === 'Home') next = order[0];
    if (e.key === 'End') next = order[order.length - 1];
    if (next) {
      e.preventDefault();
      selectTab(next);
      tabButtons.get(next).focus();
    }
  });

  const editorPane = h('section.bpane.bpane--editor', { id: 'panel-editor', role: 'tabpanel', 'aria-labelledby': 'tab-editor' });
  const editorHost = h('div.bpane-editor-host');
  const problems = h('details.problems', { open: true });
  const problemsSummary = h('summary.problems-summary');
  const problemsList = h('ul.problems-list');
  problems.append(problemsSummary, problemsList);
  editorPane.append(editorHost, problems);

  const splitter = h('div.bsplit', {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize editor and preview',
    'aria-valuemin': '20',
    'aria-valuemax': '80',
    tabindex: '0',
  });

  const previewPane = h('section.bpane.bpane--right', { id: 'panel-preview', role: 'tabpanel', 'aria-labelledby': 'tab-preview' });
  const visualPane = h('section.bpane.bpane--right.bpane--visual', { id: 'panel-visual', role: 'tabpanel', 'aria-labelledby': 'tab-visual', hidden: true });
  const jsonPane = h('section.bpane.bpane--right.bpane--json', { id: 'panel-json', role: 'tabpanel', 'aria-labelledby': 'tab-json', hidden: true });
  const jsonCode = h('code');
  const jsonPre = h('pre.json-view', { tabindex: '0' }, jsonCode);
  const jsonToolbar = h(
    'div.json-tools',
    h('span.muted.small', `$schema: ${SCHEMA_URL}`),
    h('button.button.button-sm', { type: 'button', onClick: () => copy(jsonText(), 'JSON copied') }, icon('copy'), 'Copy JSON'),
  );
  jsonPane.append(jsonToolbar, jsonPre);

  const body = h('div.bbody', tablist, editorPane, splitter, previewPane, visualPane, jsonPane);
  const dropOverlay = h('div.drop-overlay', { 'aria-hidden': 'true' }, h('div.drop-overlay-inner', icon('upload'), 'Drop an .ask or .json file to import it'));

  clear(mount);
  mount.append(toolbar, urlRow, body, dropOverlay, toast);
  installMenuBehavior(mount);

  // --- Components ----------------------------------------------------------

  const editor = createEditor(editorHost, {
    value: '',
    onChange: (text) => {
      state.source = text;
      markUnsaved();
      scheduleParse();
    },
    onSave: () => {
      if (isOwner) save();
      else {
        saveDraft.flush();
        showToast('Draft saved in this browser');
      }
    },
  });

  const preview = createPreview(previewPane, {
    onQuestionClick(id) {
      const loc = state.locations.questions[id];
      if (loc) editor.gotoLine(loc.line, { focus: false });
    },
  });

  const visual = createVisual(visualPane, {
    getForm: () => state.form,
    getLocations: () => state.locations,
    onChange: ({ structural }) => applyVisualChange(structural),
  });

  // --- Parsing and rendering ---------------------------------------------

  function applyParse() {
    const result = parse(state.source);
    state.form = result.form;
    state.diagnostics = result.diagnostics;
    state.locations = result.locations;
    afterModelChange({ fromText: true });
  }

  const scheduleParse = debounce(applyParse, PARSE_DEBOUNCE);

  function applyVisualChange(structural) {
    // The visual editor's model is authoritative here: serialize it and keep
    // it (re-parsing only for diagnostics and locations), so a transient
    // invalid state such as a duplicate option label doesn't drop rows.
    const text = serialize(state.form);
    editor.setValue(text, { keepScroll: true, silent: true });
    state.source = text;
    const result = parse(text);
    state.diagnostics = result.diagnostics;
    state.locations = result.locations;
    markUnsaved();
    afterModelChange({ fromText: false, structural });
  }

  function afterModelChange({ fromText, structural = false }) {
    editor.setDiagnostics(state.diagnostics);
    renderProblems();
    updateTitle();
    state.previewStale = true;
    state.jsonStale = true;
    if (fromText || structural) state.visualStale = true;
    renderActiveTab();
    if (isOwner) scheduleSave();
    else saveDraft();
    syncHash();
  }

  function renderActiveTab() {
    const tab = state.tab;
    if (tab === 'preview' && state.previewStale) {
      preview.render(state.form);
      state.previewStale = false;
    } else if (tab === 'visual' && state.visualStale) {
      visual.rebuild();
      state.visualStale = false;
    } else if (tab === 'json' && state.jsonStale) {
      jsonCode.textContent = jsonText();
      state.jsonStale = false;
    }
  }

  function jsonText() {
    return JSON.stringify({ $schema: SCHEMA_URL, ...state.form }, null, 2);
  }

  function updateTitle() {
    const title = state.form.title || 'Untitled form';
    if (titleEl.textContent !== title) titleEl.textContent = title;
    document.title = `${title} · Builder · Askdown`;
  }

  function renderProblems() {
    const errors = state.diagnostics.filter((d) => d.severity === 'error').length;
    const warnings = state.diagnostics.length - errors;
    clear(problemsSummary);
    clear(problemsList);
    if (!state.diagnostics.length) {
      problemsSummary.append(icon('check', 'icon-ok'), h('span', 'No problems'));
      problems.classList.remove('problems--has');
      problems.classList.remove('problems--errors');
      return;
    }
    problems.classList.add('problems--has');
    problems.classList.toggle('problems--errors', errors > 0);
    const parts = [];
    if (errors) parts.push(`${errors} error${errors === 1 ? '' : 's'}`);
    if (warnings) parts.push(`${warnings} warning${warnings === 1 ? '' : 's'}`);
    problemsSummary.append(icon(errors ? 'error' : 'warning', errors ? 'icon-error' : 'icon-warning'), h('span', parts.join(', ')));
    for (const d of state.diagnostics) {
      problemsList.append(
        h(
          'li',
          h(
            'button.problem.problem--' + d.severity,
            {
              type: 'button',
              onClick: () => {
                if (isMobile()) selectTab('editor');
                editor.gotoLine(d.line);
              },
            },
            h('span.problem-loc', `${d.line}:${d.column}`),
            h('span.problem-msg', d.message),
          ),
        ),
      );
    }
  }

  // --- Tabs ----------------------------------------------------------------

  function isMobile() {
    return matchMedia('(max-width: 860px)').matches;
  }

  function currentTabId() {
    return isMobile() && state.mobile === 'editor' ? 'editor' : state.tab;
  }

  function selectTab(id) {
    if (id === 'editor') state.mobile = 'editor';
    else {
      state.tab = id;
      state.mobile = 'right';
      storage.set(TAB_KEY, id);
    }
    const current = currentTabId();
    for (const [tid, b] of tabButtons) {
      const selected = tid === current;
      b.setAttribute('aria-selected', selected ? 'true' : 'false');
      b.tabIndex = selected ? 0 : -1;
    }
    body.dataset.tab = state.tab;
    body.dataset.mobile = state.mobile;
    previewPane.hidden = state.tab !== 'preview';
    visualPane.hidden = state.tab !== 'visual';
    jsonPane.hidden = state.tab !== 'json';
    renderActiveTab();
  }

  matchMedia('(max-width: 860px)').addEventListener('change', () => selectTab(state.mobile === 'editor' ? 'editor' : state.tab));

  // --- Splitter ------------------------------------------------------------

  let split = Number(storage.get(SPLIT_KEY)) || 50;
  function applySplit(value) {
    split = Math.max(20, Math.min(80, value));
    body.style.setProperty('--split', `${split}%`);
    splitter.setAttribute('aria-valuenow', String(Math.round(split)));
    storage.set(SPLIT_KEY, String(Math.round(split)));
  }
  applySplit(split);
  splitter.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    splitter.setPointerCapture(e.pointerId);
    body.classList.add('bbody--resizing');
    const rect = body.getBoundingClientRect();
    const move = (ev) => applySplit(((ev.clientX - rect.left) / rect.width) * 100);
    const up = () => {
      body.classList.remove('bbody--resizing');
      splitter.removeEventListener('pointermove', move);
      splitter.removeEventListener('pointerup', up);
      splitter.removeEventListener('pointercancel', up);
      editor.measure();
    };
    splitter.addEventListener('pointermove', move);
    splitter.addEventListener('pointerup', up);
    splitter.addEventListener('pointercancel', up);
  });
  splitter.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === 'ArrowLeft') applySplit(split - step);
    else if (e.key === 'ArrowRight') applySplit(split + step);
    else if (e.key === 'Home') applySplit(20);
    else if (e.key === 'End') applySplit(80);
    else if (e.key === 'Enter') applySplit(50);
    else return;
    e.preventDefault();
  });
  splitter.addEventListener('dblclick', () => applySplit(50));

  // --- Loading -------------------------------------------------------------

  /** Replaces the whole document (examples, imports, share links, publish). */
  function loadSource(text, { resetScroll = true } = {}) {
    scheduleParse.cancel();
    editor.setValue(text, { keepScroll: !resetScroll, silent: true });
    state.source = text;
    applyParse();
    if (resetScroll) preview.el.scrollTop = 0;
  }

  function loadExample(id) {
    const ex = exampleById(id);
    if (state.source.trim() && state.source !== ex.source && !confirm(`Replace the current form with "${ex.title}"?`)) return;
    loadSource(ex.source);
    markUnsaved();
    showToast(`Loaded "${ex.title}"`);
  }

  function importText(text, name) {
    let source = text;
    if (/\.json$/i.test(name) || /^\s*[{[]/.test(text)) {
      try {
        const data = JSON.parse(text);
        if (!data || typeof data !== 'object' || !Array.isArray(data.pages)) throw new Error('Not an Askdown form');
        source = serialize(data);
      } catch (err) {
        showToast(`Could not import ${name}: ${err instanceof Error ? err.message : 'invalid JSON'}`, 'error');
        return;
      }
    }
    loadSource(source);
    markUnsaved();
    showToast(`Imported ${name}`);
  }

  function importFile(file) {
    file
      .text()
      .then((text) => importText(text, file.name))
      .catch(() => showToast(`Could not read ${file.name}`, 'error'));
  }

  // Drag and drop anywhere on the builder.
  let dragDepth = 0;
  mount.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer || ![...e.dataTransfer.types].includes('Files')) return;
    dragDepth++;
    mount.classList.add('builder--dragging');
  });
  mount.addEventListener('dragleave', () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (!dragDepth) mount.classList.remove('builder--dragging');
  });
  mount.addEventListener('dragover', (e) => {
    if (e.dataTransfer && [...e.dataTransfer.types].includes('Files')) e.preventDefault();
  });
  mount.addEventListener('drop', (e) => {
    dragDepth = 0;
    mount.classList.remove('builder--dragging');
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    e.preventDefault();
    importFile(file);
  });

  // --- Export, share, copy -------------------------------------------------

  function fileStem() {
    const t = (state.form.title || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return t || 'form';
  }

  function exportAskdown() {
    downloadText(`${fileStem()}.ask`, state.source, 'text/markdown');
  }

  function exportJson() {
    downloadText(`${fileStem()}.json`, jsonText(), 'application/json');
  }

  function exportResponseSchema() {
    downloadText(`${fileStem()}.schema.json`, JSON.stringify(responseSchema(state.form), null, 2), 'application/schema+json');
  }

  function copySource() {
    copy(state.source, 'Source copied');
  }

  async function copy(text, message) {
    if (!text) return;
    const ok = await copyText(text);
    showToast(ok ? message : 'Copy failed: your browser blocked clipboard access', ok ? 'ok' : 'error');
  }

  let hashSynced = false;
  async function share() {
    const hash = await encodeHash(state.source);
    history.replaceState(null, '', `#${hash}`);
    hashSynced = true;
    const ok = await copyText(location.href);
    showToast(ok ? 'Share link copied to the clipboard' : 'Share link is in the address bar', 'ok');
  }

  const syncHash = debounce(async () => {
    if (!hashSynced) return;
    const hash = await encodeHash(state.source);
    history.replaceState(null, '', `#${hash}`);
  }, 400);

  // --- Drafts (anonymous) and saving (owners) ------------------------------

  const saveDraft = debounce(() => {
    if (isOwner) return;
    storage.set(DRAFT_KEY, state.source);
  }, 400);

  function setSaveState(s, message) {
    state.saveState = s;
    saveIndicator.dataset.state = s;
    const labels = { saved: 'Saved', saving: 'Saving…', unsaved: 'Unsaved changes', error: message || 'Could not save', draft: '' };
    saveIndicator.textContent = isOwner ? labels[s] : '';
    if (isOwner) publishButton.disabled = s === 'saving';
  }

  function markUnsaved() {
    if (!isOwner) return;
    if (state.source !== state.lastSavedSource) setSaveState('unsaved');
  }

  const scheduleSave = debounce(() => save(), SAVE_DEBOUNCE);

  let saving = null;
  async function save() {
    if (!isOwner) return true;
    scheduleSave.cancel();
    if (saving) await saving;
    if (state.source === state.lastSavedSource) {
      setSaveState('saved');
      return true;
    }
    const source = state.source;
    setSaveState('saving');
    saving = (async () => {
      try {
        const res = await fetch(`/api/forms/${formId}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ source }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        state.lastSavedSource = source;
        setSaveState(state.source === source ? 'saved' : 'unsaved');
        return true;
      } catch (err) {
        setSaveState('error', `Not saved: ${err instanceof Error ? err.message : 'network error'}`);
        return false;
      } finally {
        saving = null;
      }
    })();
    return saving;
  }

  async function publish() {
    if (state.diagnostics.some((d) => d.severity === 'error')) {
      showToast('Fix the errors in the form before publishing', 'error');
      problems.open = true;
      return;
    }
    publishButton.disabled = true;
    try {
      const saved = await save();
      if (!saved) {
        showToast('Could not save the form, so it was not published', 'error');
        return;
      }
      const res = await fetch(`/api/forms/${formId}/publish`, {
        method: 'POST',
        headers: { accept: 'application/json' },
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = Array.isArray(data.problems) && data.problems.length ? `: ${data.problems[0].message || ''}` : '';
        showToast(`${data.error || 'Publishing failed'}${detail}`, 'error');
        return;
      }
      // The server pins ids ({#id}) on publish; adopt that source.
      if (typeof data.source === 'string' && data.source !== state.source) {
        loadSource(data.source, { resetScroll: false });
        state.lastSavedSource = data.source;
      } else {
        state.lastSavedSource = state.source;
      }
      setSaveState('saved');
      state.status = 'open';
      state.publicUrl = data.url || state.publicUrl;
      renderOwnerState();
      showToast(`Published version ${data.version}. The form is live.`, 'ok');
      urlText.focus({ preventScroll: true });
    } finally {
      publishButton.disabled = state.saveState === 'saving';
    }
  }

  async function toggleStatus() {
    const action = state.status === 'open' ? 'close' : 'open';
    const res = await fetch(`/api/forms/${formId}/${action}`, { method: 'POST', headers: { accept: 'application/json' }, credentials: 'same-origin' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      showToast(data.error || 'Could not change the form status', 'error');
      return;
    }
    state.status = data.status || (action === 'open' ? 'open' : 'closed');
    renderOwnerState();
    showToast(state.status === 'open' ? 'The form is accepting responses again' : 'The form is closed to new responses');
  }

  function renderOwnerState() {
    if (!isOwner) return;
    statusBadge.className = `badge status-${state.status}`;
    statusBadge.textContent = state.status;
    statusBadge.hidden = false;
    publishButton.replaceChildren(icon('publish'), h('span', state.status === 'draft' ? 'Publish' : 'Publish changes'));
    toggleStatusButton.textContent = state.status === 'open' ? 'Close form' : 'Reopen form';
    toggleStatusButton.hidden = state.status === 'draft';
    urlRow.hidden = !state.publicUrl;
    if (state.publicUrl) {
      urlText.textContent = state.publicUrl.replace(/^https?:\/\//, '');
      urlText.href = state.publicUrl;
      urlOpen.href = state.publicUrl;
    }
  }

  window.addEventListener('beforeunload', (e) => {
    if (isOwner && state.source !== state.lastSavedSource) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // --- Toasts --------------------------------------------------------------

  let toastTimer = null;
  function showToast(message, kind = 'ok') {
    toast.textContent = message;
    toast.dataset.kind = kind;
    toast.classList.add('toast--show');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('toast--show'), kind === 'error' ? 6000 : 2800);
  }

  // --- Menus ---------------------------------------------------------------

  function menu(label, iconName, items) {
    const d = h('details.menu');
    const s = h('summary.button.button-sm', { 'aria-haspopup': 'menu', title: label }, iconName !== 'chevron' ? icon(iconName) : null, h('span', label), icon('chevron', 'icon-chevron'));
    const list = h('div.menu-list', { role: 'menu' }, items);
    d.append(s, list);
    return d;
  }

  // --- Boot ----------------------------------------------------------------

  const savedTab = storage.get(TAB_KEY);
  state.tab = savedTab === 'visual' || savedTab === 'json' ? savedTab : 'preview';
  state.mobile = 'editor';
  selectTab(state.tab);
  state.mobile = 'editor';
  if (isMobile()) selectTab('editor');
  renderOwnerState();
  setSaveState(isOwner ? 'saved' : 'draft');

  (async () => {
    const fromHash = await decodeHash(location.hash);
    if (fromHash !== null) {
      hashSynced = true;
      loadSource(fromHash);
      if (isOwner) markUnsaved();
      return;
    }
    if (isOwner) {
      loadSource(initialSource);
      return;
    }
    const draft = storage.get(DRAFT_KEY);
    loadSource(draft !== null && draft.trim() !== '' ? draft : EXAMPLES[0].source);
  })();

  window.addEventListener('hashchange', async () => {
    const fromHash = await decodeHash(location.hash);
    if (fromHash !== null && fromHash !== state.source) {
      hashSynced = true;
      loadSource(fromHash);
      markUnsaved();
    }
  });

  // Test hook (used by the in-page self-test): synchronous access to state.
  window.__askdownBuilder = {
    state,
    editor,
    preview,
    visual,
    selectTab,
    loadSource,
    flush() {
      scheduleParse.flush();
      renderActiveTab();
    },
    setEditorText(text) {
      editor.setValue(text, { keepScroll: true, silent: false });
    },
    encodeHash,
    decodeHash,
  };
}
